import { format, parseISO } from 'date-fns';

import { cn } from '@/lib/shadcn/utils';
import type { ShortlistCandidateDetail } from '@/modules/public-shortlist/mapPublicCandidate';

type ShortlistWorkExperience =
  ShortlistCandidateDetail['workExperience'][number];

interface ShortlistWorkHistoryProps {
  workExperience: ShortlistWorkExperience[];
}

function formatDate(date: string | null): string | null {
  if (!date) return null;

  try {
    return format(parseISO(date), 'MMMM yyyy');
  } catch {
    return null;
  }
}

export function ShortlistWorkHistory({
  workExperience,
}: ShortlistWorkHistoryProps) {
  return (
    <div>
      {workExperience.map((experience, index) => (
        <ShortlistWorkHistoryItem
          key={`${experience.companyName}-${experience.jobTitle}-${index}`}
          experience={experience}
          isLast={index === workExperience.length - 1}
        />
      ))}
    </div>
  );
}

interface ShortlistWorkHistoryItemProps {
  experience: ShortlistWorkExperience;
  isLast: boolean;
}

function ShortlistWorkHistoryItem({
  experience,
  isLast,
}: ShortlistWorkHistoryItemProps) {
  const startLabel = formatDate(experience.startDate) ?? 'Unspecified';
  const endLabel = experience.endDate ? formatDate(experience.endDate) : 'Now';

  return (
    <div className="relative flex gap-4">
      <div className="flex flex-col items-center">
        <div className="border-primary mt-1.5 size-3 shrink-0 rounded-full border-2 bg-white" />
        {!isLast && <div className="min-h-8 w-0.5 flex-1 bg-neutral-300" />}
      </div>

      <div className={cn('flex-1', !isLast && 'pb-4')}>
        <p className="text-primary-700 text-sm font-semibold">
          {experience.jobTitle}
        </p>
        <p className="text-neutral-650 mt-0.5 text-xs">
          {startLabel} - {endLabel}
          {experience.companyName && <> | {experience.companyName}</>}
        </p>
      </div>
    </div>
  );
}
